/**
 * Kurze Datei-Referenzen im Markdown.
 *
 * Statt der kompletten PocketBase-Adresse
 *   ![Foto](https://xyz.pocketbasecloud.com/api/files/materials/abc123/foto.jpg)
 * steht im Quelltext nur noch
 *   ![Foto](pb:abc123/foto.jpg)
 *
 * Aufgelöst wird erst beim Rendern. Der Markdown-Layer kennt PocketBase dabei
 * nicht selbst – die App meldet beim Start einen Auflöser an (siehe main.ts),
 * damit die Service-Schicht die einzige Backend-Zugriffsstelle bleibt.
 */

export const FILE_REF_SCHEME = "pb:";

/**
 * Markierung "aus der Vorschau/dem Export ausgeblendet" – als URL-Fragment an
 * die Kurzform gehängt (`pb:<id>/<datei>#hidden`), damit es eine ganz normale
 * Markdown-Bildsyntax bleibt und nicht extra escaped werden muss. Betrifft
 * nur die Anzeige (siehe markdown/index.ts); der Auflöser hier schneidet es
 * vor dem eigentlichen Auflösen wieder ab.
 */
const HIDDEN_SUFFIX = "#hidden";

export function isHiddenRef(src: string): boolean {
  return src.startsWith(FILE_REF_SCHEME) && src.endsWith(HIDDEN_SUFFIX);
}

/** Blendet einen Datei-Verweis für Vorschau/Export ein oder aus. */
export function toggleHiddenRef(src: string): string {
  if (!src.startsWith(FILE_REF_SCHEME)) return src;
  return isHiddenRef(src) ? src.slice(0, -HIDDEN_SUFFIX.length) : src + HIDDEN_SUFFIX;
}

type Resolver = (ref: string) => string;

let resolver: Resolver | null = null;

export function setFileRefResolver(fn: Resolver): void {
  resolver = fn;
}

let imageUrlHook: Resolver | null = null;

export function setImageUrlHook(fn: Resolver): void {
  imageUrlHook = fn;
}

export function displayImageUrl(url: string): string {
  return imageUrlHook ? imageUrlHook(url) : url;
}

/** Prozent-Kodierung rückgängig machen, ohne bei kaputten Sequenzen zu werfen. */
function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** `pb:<recordId>/<datei>` -> echte URL. Alles andere bleibt unverändert. */
export function resolveFileRef(src: string): string {
  if (!src.startsWith(FILE_REF_SCHEME)) return src;
  if (!resolver) return src;
  const ref = (isHiddenRef(src) ? src.slice(0, -HIDDEN_SUFFIX.length) : src).slice(FILE_REF_SCHEME.length);
  const slash = ref.indexOf("/");
  if (slash < 1) return src;
  const decoded = ref.slice(0, slash) + "/" + safeDecode(ref.slice(slash + 1));
  return resolver(decoded) || src;
}

/**
 * Baut die Kurzform aus Record-Id und Dateinamen. Der Dateiname wird kodiert,
 * damit Leerzeichen die Markdown-Link-Syntax nicht sprengen.
 */
export function fileRef(recordId: string, filename: string): string {
  return `${FILE_REF_SCHEME}${recordId}/${encodeURIComponent(filename)}`;
}

/**
 * Wandelt eine bereits eingefügte lange PocketBase-Datei-URL in die Kurzform
 * um (für Altbestände / eingefügte Links).
 */
export function shortenFileUrl(url: string): string {
  const m = /\/api\/files\/[^/]+\/([^/?#]+)\/([^/?#]+)/.exec(url);
  return m ? `${FILE_REF_SCHEME}${m[1]}/${m[2]}` : url;
}
